export type AdminSession = {
  authMethod: 'apiKey' | 'oauth';
  name: string;
  expiresAt: number;
  apiKey?: string;
  accessToken?: string;
};

const STORAGE_KEY = 'admin_session_v2';
const LEGACY_STORAGE_KEY = 'admin_session_v1';
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

type LegacyAdminSession = {
  apiKey: string;
  name: string;
  expiresAt: number;
};

function isExpired(session: AdminSession): boolean {
  return Date.now() > session.expiresAt;
}

function isValidSession(session: AdminSession | null): session is AdminSession {
  if (!session?.name || !session.expiresAt) return false;

  if (session.authMethod === 'apiKey') {
    return Boolean(session.apiKey);
  }

  if (session.authMethod === 'oauth') {
    return Boolean(session.accessToken);
  }

  return false;
}

function migrateLegacySession(raw: string): AdminSession | null {
  try {
    const parsed = JSON.parse(raw) as LegacyAdminSession;
    if (!parsed?.apiKey || !parsed?.expiresAt) return null;

    return {
      authMethod: 'apiKey',
      apiKey: parsed.apiKey,
      name: parsed.name || 'Admin',
      expiresAt: parsed.expiresAt,
    };
  } catch {
    return null;
  }
}

function readStoredSession(): AdminSession | null {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (raw) {
    try {
      return JSON.parse(raw) as AdminSession;
    } catch {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
  }

  const legacyRaw = localStorage.getItem(LEGACY_STORAGE_KEY);
  if (!legacyRaw) return null;

  const migrated = migrateLegacySession(legacyRaw);
  if (!migrated) {
    localStorage.removeItem(LEGACY_STORAGE_KEY);
    return null;
  }

  localStorage.setItem(STORAGE_KEY, JSON.stringify(migrated));
  localStorage.removeItem(LEGACY_STORAGE_KEY);
  return migrated;
}

export function setAdminSession(apiKey: string, name: string): void {
  const session: AdminSession = {
    authMethod: 'apiKey',
    apiKey,
    name,
    expiresAt: Date.now() + MAX_AGE_MS,
  };

  localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
}

export function setOAuthSession(accessToken: string, name: string): void {
  const session: AdminSession = {
    authMethod: 'oauth',
    accessToken,
    name,
    expiresAt: Date.now() + MAX_AGE_MS,
  };

  localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
}

export function getAdminSession(): AdminSession | null {
  const session = readStoredSession();
  if (!isValidSession(session)) {
    clearAdminSession();
    return null;
  }

  if (isExpired(session)) {
    clearAdminSession();
    return null;
  }

  return session;
}

export function clearAdminSession(): void {
  localStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem(LEGACY_STORAGE_KEY);
}
